import { Box, SxProps, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  colorLandingBlue,
  colorLandingEvent,
  colorLandingHeatmapBaseRgb,
  colorLandingInk,
  colorLandingMuted,
  colorLandingSurface,
  colorLandingText,
  fontLandingDisplay,
} from "src/design/tokens";

const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

export interface PerformanceRow {
  label: string;
  kind: "offer" | "event";
  /** Covers per weekday, Monday first; `null` means the offer did not run. */
  covers: (number | null)[];
  totalCovers: number;
  delta: string;
}

interface OfferPerformanceTableProps {
  title: string;
  rows: PerformanceRow[];
  sx?: SxProps;
}

const KIND_COLORS: Record<PerformanceRow["kind"], string> = {
  offer: colorLandingBlue,
  event: colorLandingEvent,
};

// Cells are tinted proportionally to the busiest day in the table, so a full
// cell always reads as "this was the peak".
const cellFill = (covers: number, peak: number) =>
  `rgba(${colorLandingHeatmapBaseRgb}, ${(0.15 + 0.73 * (covers / peak)).toFixed(2)})`;

/**
 * Weekday heatmap of the covers an offer or event generated, mirroring the
 * offer-performance table restaurants get inside the dashboard.
 */
export const OfferPerformanceTable = ({
  title,
  rows,
  sx,
}: OfferPerformanceTableProps) => {
  const { t } = useTranslation();

  const peak = Math.max(
    1,
    ...rows.flatMap((row) => row.covers.filter((c): c is number => c !== null))
  );

  return (
    <Box sx={{ ...sx }}>
      <Typography
        sx={{
          fontFamily: fontLandingDisplay,
          fontSize: 16,
          color: colorLandingInk,
          mb: 1.5,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ overflowX: "auto" }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: 560,
            borderCollapse: "separate",
            borderSpacing: "3px 0",
            fontSize: 12,
          }}
        >
          <Box component="thead">
            <Box component="tr">
              <Box
                component="th"
                sx={{
                  color: colorLandingMuted,
                  fontWeight: 600,
                  fontSize: 11,
                  p: 0.75,
                  textAlign: "left",
                }}
              >
                {t("landingPage.analyticsSection.offerColumn")}
              </Box>
              {WEEKDAY_KEYS.map((day) => (
                <Box
                  component="th"
                  key={day}
                  sx={{
                    color: colorLandingMuted,
                    fontWeight: 600,
                    fontSize: 11,
                    p: 0.75,
                    textAlign: "center",
                  }}
                >
                  {t(`landingPage.analyticsSection.shortDays.${day}`)}
                </Box>
              ))}
              <Box
                component="th"
                sx={{
                  color: colorLandingMuted,
                  fontWeight: 600,
                  fontSize: 11,
                  p: 0.75,
                  textAlign: "center",
                }}
              >
                {t("landingPage.analyticsSection.coversColumn")}
              </Box>
              <Box
                component="th"
                sx={{
                  color: colorLandingMuted,
                  fontWeight: 600,
                  fontSize: 11,
                  p: 0.75,
                  textAlign: "center",
                }}
              >
                {t("landingPage.analyticsSection.deltaColumn")}
              </Box>
            </Box>
          </Box>

          <Box component="tbody">
            {rows.map((row) => (
              <Box component="tr" key={row.label}>
                <Box
                  component="td"
                  sx={{
                    p: 1,
                    textAlign: "left",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    color: colorLandingText,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.875 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "3px",
                        backgroundColor: KIND_COLORS[row.kind],
                      }}
                    />
                    {row.label}
                  </Box>
                </Box>

                {row.covers.map((covers, index) => (
                  <Box
                    component="td"
                    key={WEEKDAY_KEYS[index]}
                    sx={{
                      p: 1,
                      textAlign: "center",
                      fontWeight: covers === null ? 400 : 700,
                      borderRadius: "6px",
                      color:
                        covers === null
                          ? colorLandingMuted
                          : covers / peak > 0.7
                          ? colorLandingSurface
                          : colorLandingInk,
                      backgroundColor:
                        covers === null ? "transparent" : cellFill(covers, peak),
                    }}
                  >
                    {covers === null ? "–" : covers}
                  </Box>
                ))}

                <Box
                  component="td"
                  sx={{
                    p: 1,
                    fontWeight: 800,
                    color: colorLandingInk,
                    textAlign: "center",
                  }}
                >
                  {row.totalCovers}
                </Box>
                <Box
                  component="td"
                  sx={{ p: 1, textAlign: "center", color: colorLandingText }}
                >
                  {row.delta}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
